import React, { ComponentProps } from 'react';
import IForm from '../../../types/IForm';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { PostFirst, PostsList, PostWithReplies } from '../../../_lib/components/Posts';
import { asView } from '../../../_lib/ViewFetcher';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { CommentsMainEditor } from '../partials/CommentsMainEditor';
import { useBlockContext } from '../../../_lib/BlockFetcher';
import { BlockType } from '../../../types/api/ElementType';
import { postType, PostBlockTypes, PostFormTypes } from '../../../_lib/components/Posts/Post.types';
import { SonForm } from '../../../_lib/Form2';
import { HeadingHierarchy } from '../../../_lib/components/Heading/Heading';

type DefaultViewProps = { createForm: IForm; posts: PostFirst[]; active: true } | { active: false };

function CommentsDefault(props: DefaultViewProps) {
  const { elementType } = useBlockContext();
  const { t } = useSonTranslation(elementType === BlockType.comments ? 'CommentsBlock' : 'EditorFeedback');
  const containerModifier = elementType === BlockType.comments ? 'posts--comments' : 'posts--editor-feedback';

  const {
    userInfo,
    editModeInfo: { active },
  } = useAppContext();

  if (!props.active) {
    return active
      ? (
          <div className="posts__activation-info font-regular-15-19">
            <p>{t('default.disabled')}</p>
          </div>
        )
      : null;
  }

  const { createForm, posts } = props;
  // if edit mode is active, and we have no comments, we do not want to show anything
  if (active && (!posts || posts.length === 0)) {
    return (
      <div className="posts__activation-info font-regular-15-19">
        <p>{t('default.enabled')}</p>
      </div>
    );
  }

  /**
   * Heading hierachys
   */
  const headingHierarchy: HeadingHierarchy = 2;
  const subHeadingsHierarchy: HeadingHierarchy = headingHierarchy + 1 as HeadingHierarchy;
  const commentHeadingsHierarchy: HeadingHierarchy = subHeadingsHierarchy + 1 as HeadingHierarchy;

  /**
   * different default view in edit mode to inform user of activation
   * no information if disabled
   * */
  return (
    <div className={`posts ${containerModifier}`}>
      {!active && userInfo?.loggedIn && (
        <CommentsMainEditor
          subHeadingsHierarchy={subHeadingsHierarchy}
          createForm={createForm as SonForm<PostFormTypes>}
        />
      )}
      <PostsList headingHierarchy={subHeadingsHierarchy} type={postType(elementType as PostBlockTypes)}>
        {posts.map((comment, index) => {
          return (
            <PostWithReplies
              key={comment.id}
              post={comment}
              index={index}
              headingHierarchy={commentHeadingsHierarchy}
              previousPostId={index > 0 ? posts[index - 1].id : undefined}
              type={postType(elementType as PostBlockTypes)}
            />
          );
        })}
      </PostsList>
    </div>
  );
}

export const CommentsDefaultView = asView(CommentsDefault);
export type CommentsDefaultView = ComponentProps<typeof CommentsDefaultView>;
